// 2026-07-07: PMI Special-Process — Jigging & Test Panels.
// Two model additions the real router (job 1600090239) exposed:
//   • Jigging (Route Card Op 50, PGT-SP-ST-SOP-10 / form PGT-SP-ST-F-13) — jig type/number/continuity/qty
//   • Test-panel companion job — coupons (2x 2024-Al) processed WITH every batch for adhesion/destructive test;
//     parent WO 1600090239 -> panel job 1600090240. Panel adhesion PASS gates parent-batch dispatch.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Tooltip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d); if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}
const RESULT = {
  PASS:    { color: BR.green, bg: '#dcfce7', label: '✓ PASS' },
  FAIL:    { color: BR.red,   bg: '#fee2e2', label: '✗ FAIL' },
  PENDING: { color: BR.amber, bg: '#fef3c7', label: '⏳ PENDING' },
};
const emptyJig = () => ({ id: '', route_card_no: '', wo_number: '', part_no: '', jig_type: '', jig_number: '', continuity_color: '', no_of_jigs: '', operator: '', notes: '', ts: '' });
const emptyPanel = () => ({ id: '', parent_wo_number: '', route_card_no: '', part_no: '', panel_job_no: '', coupon_spec: '2 nos 2024-Al 12x4x0.032in', process: '', adhesion_result: 'PENDING', thickness_micron: '', test_report_no: '', test_report_date: '', notes: '', ts: '' });

export default function JiggingPanels() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [tab, setTab] = useState('JIG');
  const [jigs, setJigs] = useState([]);
  const [panels, setPanels] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jigOpen, setJigOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [jigDraft, setJigDraft] = useState(emptyJig());
  const [panelDraft, setPanelDraft] = useState(emptyPanel());
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [jg, pn, rc] = await Promise.all([
        customerbasedshift(customerId, 'jig_records').catch(() => []),
        customerbasedshift(customerId, 'panel_jobs').catch(() => []),
        customerbasedshift(customerId, 'route_cards').catch(() => []),
      ]);
      setJigs(Array.isArray(jg?.[0]?.value) ? jg[0].value : []);
      setPanels(Array.isArray(pn?.[0]?.value) ? pn[0].value : []);
      setCards(Array.isArray(rc?.[0]?.value) ? rc[0].value : []);
    } finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { refresh(); }, [refresh]);

  const cardByRc = useMemo(() => { const m = {}; cards.forEach(c => { m[c.route_card_no] = c; }); return m; }, [cards]);

  async function persistJigs(next) { await shiftadd({ jig_records: next }, customerId, 'SERVER_SCOPE'); setJigs(next); }
  async function persistPanels(next) { await shiftadd({ panel_jobs: next }, customerId, 'SERVER_SCOPE'); setPanels(next); }

  // ---- jigging ----
  function openJigAdd() { setJigDraft({ ...emptyJig(), id: `jig-${Date.now()}` }); setJigOpen(true); }
  function openJigEdit(x) { setJigDraft({ ...emptyJig(), ...x }); setJigOpen(true); }
  function jigPickCard(rc) { const c = cardByRc[rc]; setJigDraft(d => ({ ...d, route_card_no: rc, wo_number: c?.wo_number || d.wo_number, part_no: c?.part_no || d.part_no })); }
  async function saveJig() {
    if (!jigDraft.jig_number) { Swal.fire({ icon: 'warning', title: 'Jig number required', confirmButtonColor: BR.orange }); return; }
    setSaving(true);
    try {
      const rec = { ...jigDraft, ts: jigDraft.ts || new Date().toISOString() };
      const i = jigs.findIndex(x => x.id === rec.id);
      await persistJigs(i === -1 ? [...jigs, rec] : jigs.map((x, k) => k === i ? rec : x));
      setJigOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false, icon: 'success', title: `Jig ${rec.jig_number} saved` });
    } catch (e) { Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange }); }
    finally { setSaving(false); }
  }

  // ---- panels ----
  function openPanelAdd() { setPanelDraft({ ...emptyPanel(), id: `pnl-${Date.now()}` }); setPanelOpen(true); }
  function openPanelEdit(x) { setPanelDraft({ ...emptyPanel(), ...x }); setPanelOpen(true); }
  function panelPickCard(rc) { const c = cardByRc[rc]; setPanelDraft(d => ({ ...d, route_card_no: rc, parent_wo_number: c?.wo_number || d.parent_wo_number, part_no: c?.part_no || d.part_no })); }
  async function savePanel() {
    if (!panelDraft.panel_job_no) { Swal.fire({ icon: 'warning', title: 'Panel job no required', confirmButtonColor: BR.orange }); return; }
    setSaving(true);
    try {
      const rec = { ...panelDraft, ts: panelDraft.ts || new Date().toISOString() };
      const i = panels.findIndex(x => x.id === rec.id);
      await persistPanels(i === -1 ? [...panels, rec] : panels.map((x, k) => k === i ? rec : x));
      setPanelOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false, icon: 'success', title: `Panel job ${rec.panel_job_no} saved` });
    } catch (e) { Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange }); }
    finally { setSaving(false); }
  }

  const panelKpi = useMemo(() => ({ total: panels.length, pass: panels.filter(p => p.adhesion_result === 'PASS').length, fail: panels.filter(p => p.adhesion_result === 'FAIL').length, pending: panels.filter(p => p.adhesion_result === 'PENDING').length }), [panels]);
  const rcOptions = cards.map(c => c.route_card_no);

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>🧷 Jigging &amp; Test Panels</h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Jig setup (Op 50 · PGT-SP-ST-F-13) &amp; companion test coupons — adhesion PASS gates batch dispatch
          </div>
        </div>
        <button onClick={tab === 'JIG' ? openJigAdd : openPanelAdd}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: BR.orange, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> {tab === 'JIG' ? 'Record Jigging' : 'Add Panel Job'}
        </button>
      </div>

      {/* tab toggle */}
      <div style={{ display: 'inline-flex', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
        {[{ k: 'JIG', l: `Jigging (${jigs.length})` }, { k: 'PANEL', l: `Test Panels (${panels.length})` }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding: '8px 18px', border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer', background: tab === t.k ? BR.orange : '#fff', color: tab === t.k ? '#fff' : BR.muted }}>{t.l}</button>
        ))}
      </div>

      {tab === 'PANEL' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
          {[{ label: 'Panel jobs', v: panelKpi.total, c: BR.dark }, { label: 'Adhesion pass', v: panelKpi.pass, c: BR.green }, { label: 'Pending', v: panelKpi.pending, c: BR.amber }, { label: 'Fail', v: panelKpi.fail, c: BR.red }].map(t => (
            <div key={t.label} style={{ background: '#fff', border: `2px solid ${BR.border}`, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: t.c, lineHeight: 1.1, marginTop: 4 }}>{t.v}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        : tab === 'JIG' ? (
          jigs.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>No jigging records yet. Click "Record Jigging".</div>
          : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Route Card', 'WO', 'Part', 'Jig Type', 'Jig Number', 'Continuity/Colour', 'No. Jigs', 'Operator', 'When', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>))}
              </tr></thead>
              <tbody>{jigs.slice().sort((a, b) => new Date(b.ts) - new Date(a.ts)).map(x => (
                <tr key={x.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{x.route_card_no || '—'}</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace' }}>{x.wo_number || '—'}</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace' }}>{x.part_no || '—'}</td>
                  <td style={{ padding: '9px 10px' }}>{x.jig_type || '—'}</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{x.jig_number}</td>
                  <td style={{ padding: '9px 10px' }}>{x.continuity_color || '—'}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700 }}>{x.no_of_jigs || '—'}</td>
                  <td style={{ padding: '9px 10px' }}>{x.operator || '—'}</td>
                  <td style={{ padding: '9px 10px', fontSize: 11, color: BR.muted }}>{fmtDate(x.ts)}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right' }}><Tooltip title="Edit"><IconButton size="small" onClick={() => openJigEdit(x)}><EditIcon fontSize="small" sx={{ color: '#111' }} /></IconButton></Tooltip></td>
                </tr>))}
              </tbody>
            </table>
        ) : (
          panels.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>No panel jobs yet. Click "Add Panel Job".</div>
          : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Adhesion', 'Panel Job', 'Parent WO', 'Part', 'Coupon Spec', 'Process', 'Thk µm', 'Test Report', 'When', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>))}
              </tr></thead>
              <tbody>{panels.slice().sort((a, b) => new Date(b.ts) - new Date(a.ts)).map(x => {
                const r = RESULT[x.adhesion_result] || RESULT.PENDING;
                return (<tr key={x.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                  <td style={{ padding: '9px 10px' }}><span style={{ padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10, background: r.bg, color: r.color, border: `1px solid ${r.color}` }}>{r.label}</span></td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{x.panel_job_no}</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{x.parent_wo_number || '—'}</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace' }}>{x.part_no || '—'}</td>
                  <td style={{ padding: '9px 10px', color: BR.muted }}>{x.coupon_spec || '—'}</td>
                  <td style={{ padding: '9px 10px' }}>{x.process || '—'}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right' }}>{x.thickness_micron || '—'}</td>
                  <td style={{ padding: '9px 10px' }}>{x.test_report_no ? <span>{x.test_report_no}<div style={{ fontSize: 10, color: BR.muted }}>{x.test_report_date || ''}</div></span> : '—'}</td>
                  <td style={{ padding: '9px 10px', fontSize: 11, color: BR.muted }}>{fmtDate(x.ts)}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right' }}><Tooltip title="Edit"><IconButton size="small" onClick={() => openPanelEdit(x)}><EditIcon fontSize="small" sx={{ color: '#111' }} /></IconButton></Tooltip></td>
                </tr>);
              })}
              </tbody>
            </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6 }}>
        <strong style={{ color: '#1e3a8a' }}>Note:</strong> {tab === 'JIG'
          ? 'Jigging (Op 50) records the fixture used per route card — jig number, continuity/colour and quantity — for full fixture traceability (form PGT-SP-ST-F-13).'
          : 'Each aerospace batch carries 2 companion coupons (e.g. 2024-Al panels) processed alongside the parts. Their adhesion / thickness results prove the process; adhesion PASS is required before the parent batch is dispatched.'}
      </div>

      {/* ---- Jigging dialog ---- */}
      <Dialog open={jigOpen} onClose={() => setJigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{jigs.some(x => x.id === jigDraft.id) ? 'Edit jigging' : 'Record jigging (Op 50)'}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Route Card" size="small" fullWidth select value={jigDraft.route_card_no}
              onChange={e => jigPickCard(e.target.value)} sx={{ gridColumn: '1 / span 2' }}>
              <MenuItem value="">— None —</MenuItem>
              {rcOptions.map(rc => <MenuItem key={rc} value={rc}>{rc}</MenuItem>)}
            </TextField>
            <TextField label="WO Number" size="small" fullWidth value={jigDraft.wo_number} onChange={e => setJigDraft({ ...jigDraft, wo_number: e.target.value })} />
            <TextField label="Part No" size="small" fullWidth value={jigDraft.part_no} onChange={e => setJigDraft({ ...jigDraft, part_no: e.target.value })} />
            <TextField label="Jig Type" size="small" fullWidth value={jigDraft.jig_type} onChange={e => setJigDraft({ ...jigDraft, jig_type: e.target.value })} placeholder="Titanium wire / rack" />
            <TextField label="Jig Number *" size="small" fullWidth value={jigDraft.jig_number} onChange={e => setJigDraft({ ...jigDraft, jig_number: e.target.value })} placeholder="JG-TI-2301 to 2310" />
            <TextField label="Continuity / Colour" size="small" fullWidth value={jigDraft.continuity_color} onChange={e => setJigDraft({ ...jigDraft, continuity_color: e.target.value })} />
            <TextField label="No. of Jigs" size="small" fullWidth type="number" value={jigDraft.no_of_jigs} onChange={e => setJigDraft({ ...jigDraft, no_of_jigs: e.target.value })} />
            <TextField label="Operator" size="small" fullWidth value={jigDraft.operator} onChange={e => setJigDraft({ ...jigDraft, operator: e.target.value })} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={jigDraft.notes} onChange={e => setJigDraft({ ...jigDraft, notes: e.target.value })} sx={{ gridColumn: '1 / span 2' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJigOpen(false)}>Cancel</Button>
          <Button onClick={saveJig} disabled={saving} variant="contained" sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Panel dialog ---- */}
      <Dialog open={panelOpen} onClose={() => setPanelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{panels.some(x => x.id === panelDraft.id) ? 'Edit panel job' : 'Add companion panel job'}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Route Card" size="small" fullWidth select value={panelDraft.route_card_no}
              onChange={e => panelPickCard(e.target.value)} sx={{ gridColumn: '1 / span 2' }}>
              <MenuItem value="">— None —</MenuItem>
              {rcOptions.map(rc => <MenuItem key={rc} value={rc}>{rc}</MenuItem>)}
            </TextField>
            <TextField label="Parent WO" size="small" fullWidth value={panelDraft.parent_wo_number} onChange={e => setPanelDraft({ ...panelDraft, parent_wo_number: e.target.value })} placeholder="1600090239" />
            <TextField label="Panel/PAT Job No *" size="small" fullWidth value={panelDraft.panel_job_no} onChange={e => setPanelDraft({ ...panelDraft, panel_job_no: e.target.value })} placeholder="1600090240" />
            <TextField label="Part No" size="small" fullWidth value={panelDraft.part_no} onChange={e => setPanelDraft({ ...panelDraft, part_no: e.target.value })} />
            <TextField label="Process" size="small" fullWidth value={panelDraft.process} onChange={e => setPanelDraft({ ...panelDraft, process: e.target.value })} placeholder="BSA / Chem-Conv" />
            <TextField label="Coupon Spec" size="small" fullWidth value={panelDraft.coupon_spec} onChange={e => setPanelDraft({ ...panelDraft, coupon_spec: e.target.value })} sx={{ gridColumn: '1 / span 2' }} />
            <TextField label="Adhesion Result" size="small" fullWidth select value={panelDraft.adhesion_result} onChange={e => setPanelDraft({ ...panelDraft, adhesion_result: e.target.value })}>
              <MenuItem value="PENDING">⏳ Pending</MenuItem>
              <MenuItem value="PASS">✓ Pass</MenuItem>
              <MenuItem value="FAIL">✗ Fail</MenuItem>
            </TextField>
            <TextField label="Thickness (µm)" size="small" fullWidth type="number" value={panelDraft.thickness_micron} onChange={e => setPanelDraft({ ...panelDraft, thickness_micron: e.target.value })} />
            <TextField label="Test Report No" size="small" fullWidth value={panelDraft.test_report_no} onChange={e => setPanelDraft({ ...panelDraft, test_report_no: e.target.value })} />
            <TextField label="Test Report Date" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={panelDraft.test_report_date} onChange={e => setPanelDraft({ ...panelDraft, test_report_date: e.target.value })} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={panelDraft.notes} onChange={e => setPanelDraft({ ...panelDraft, notes: e.target.value })} sx={{ gridColumn: '1 / span 2' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPanelOpen(false)}>Cancel</Button>
          <Button onClick={savePanel} disabled={saving} variant="contained" sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
